import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { Popover, PopoverContent, PopoverTrigger } from 'src/@/components/ui/popover';
import { resolvePlatform } from 'src/design/tokens';
import { MKT_POSTS_DESIGN as d } from './MktPosts.design';
import { STATUS_MAP } from './constants';

const MAX_VISIBLE_PER_DAY = 3;

const CHIP_TONE = {
    gray: 'bg-[#f4f4f7] text-[#52525b] border-l-[#a1a1aa] hover:bg-[#ececef]',
    blue: 'bg-blue-50 text-blue-800 border-l-blue-500 hover:bg-blue-100/80',
    amber: 'bg-amber-50 text-amber-800 border-l-amber-500 hover:bg-amber-100/80',
    green: 'bg-emerald-50 text-emerald-800 border-l-emerald-500 hover:bg-emerald-100/80',
    red: 'bg-red-50 text-red-800 border-l-red-500 hover:bg-red-100/80',
};

function pad2(n) {
    return String(n).padStart(2, '0');
}

function dateKey(date) {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getPostDate(post) {
    const raw = post.scheduled_date || post.published_date;
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
}

function formatTime(date, locale) {
    if (!date) return '';
    if (date.getHours() === 0 && date.getMinutes() === 0) return '';
    return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

function truncate(text, maxLen = 28) {
    if (!text) return '';
    const clean = text.replace(/\s+/g, ' ').trim();
    return clean.length > maxLen ? `${clean.slice(0, maxLen)}…` : clean;
}

function getCalendarCells(year, month) {
    const firstDay = new Date(year, month, 1);
    const startOffset = firstDay.getDay();
    return Array.from({ length: 42 }, (_, i) => {
        const date = new Date(year, month, i - startOffset + 1);
        return { date, inMonth: date.getMonth() === month };
    });
}

export default function MktPostsCalendar({ posts, onOpenPost, onCreateAt }) {
    const { t, i18n } = useTranslation('marketing');
    const today = useMemo(() => new Date(), []);
    const [currentMonth, setCurrentMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

    const { postsByDay, postsWithoutDate, monthCount } = useMemo(() => {
        const byDay = {};
        const withoutDate = [];
        let countInMonth = 0;
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();

        (posts || []).forEach((post) => {
            const date = getPostDate(post);
            if (!date) {
                withoutDate.push(post);
                return;
            }
            const key = dateKey(date);
            if (!byDay[key]) byDay[key] = [];
            byDay[key].push(post);
            if (date.getFullYear() === year && date.getMonth() === month) {
                countInMonth += 1;
            }
        });

        Object.keys(byDay).forEach((key) => {
            byDay[key].sort((a, b) => getPostDate(a) - getPostDate(b));
        });

        return { postsByDay: byDay, postsWithoutDate: withoutDate, monthCount: countInMonth };
    }, [posts, currentMonth]);

    const cells = useMemo(
        () => getCalendarCells(currentMonth.getFullYear(), currentMonth.getMonth()),
        [currentMonth]
    );

    const monthLabel = useMemo(() => {
        const label = currentMonth.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
        return label.charAt(0).toUpperCase() + label.slice(1);
    }, [currentMonth, i18n.language]);

    const navigateMonth = (delta) => {
        setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
    };

    const goToday = () => {
        setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    };

    const renderPostChip = (post, { inPopover = false } = {}) => {
        const status = STATUS_MAP[post.status] || STATUS_MAP.draft;
        const toneClass = CHIP_TONE[status.tone] || CHIP_TONE.gray;
        const date = getPostDate(post);
        const time = formatTime(date, i18n.language);
        const label = truncate(post.content?.text) || t(status.labelKey);

        return (
            <button
                key={post._id}
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpenPost?.(post); }}
                title={post.content?.text || t(status.labelKey)}
                className={`${d.calendar.chip} ${inPopover ? d.calendar.chipInPopover : ''} border-l-2 ${toneClass}`}
            >
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${status.dot}`} />
                {time && <span className="tabular-nums flex-shrink-0 font-semibold opacity-80">{time}</span>}
                <span className="truncate">{label}</span>
                <span className="ml-auto flex items-center gap-0.5 flex-shrink-0">
                    {(post.platforms || []).slice(0, 2).map((platform) => {
                        const info = resolvePlatform(platform);
                        return info ? (
                            <Iconify key={platform} icon={info.icon} width={11} style={{ color: info.color }} />
                        ) : null;
                    })}
                </span>
            </button>
        );
    };

    const renderDayPopover = (date, dayPosts) => (
        <PopoverContent
            align="start"
            className="w-80 p-2 rounded-xl border border-[#e7e7ec] shadow-lg"
            onClick={(e) => e.stopPropagation()}
        >
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#71717a] px-1 pb-2">
                {date.toLocaleDateString(i18n.language, { weekday: 'long', day: '2-digit', month: 'long' })}
            </p>
            <div className="flex flex-col gap-1 max-h-72 overflow-y-auto">
                {dayPosts.map((post) => renderPostChip(post, { inPopover: true }))}
            </div>
            <button
                type="button"
                className={d.calendar.popoverCreateBtn}
                onClick={() => onCreateAt?.(new Date(date.getFullYear(), date.getMonth(), date.getDate(), 9, 0))}
            >
                <Iconify icon="eva:plus-outline" width={14} />
                {t('posts.calendar.createOnDay')}
            </button>
        </PopoverContent>
    );

    return (
        <div className={d.calendar.container}>
            <div className={d.calendar.toolbar}>
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <div className={d.calendar.navGroup}>
                        <button type="button" onClick={() => navigateMonth(-1)} className={d.calendar.navBtn} aria-label={t('posts.calendar.prevMonth')}>
                            <Iconify icon="eva:chevron-left-outline" width={18} />
                        </button>
                        <button type="button" onClick={() => navigateMonth(1)} className={d.calendar.navBtn} aria-label={t('posts.calendar.nextMonth')}>
                            <Iconify icon="eva:chevron-right-outline" width={18} />
                        </button>
                    </div>
                    <button type="button" className={d.calendar.todayBtn} onClick={goToday}>
                        {t('posts.calendar.today')}
                    </button>
                    <div className="min-w-0">
                        <h3 className={d.calendar.monthTitle}>{monthLabel}</h3>
                        <p className={d.calendar.monthMeta}>
                            {monthCount === 0 && postsWithoutDate.length === 0
                                ? t('posts.calendar.emptyMonth')
                                : t('posts.calendar.postsInMonth', { count: monthCount })}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap justify-end">
                    {postsWithoutDate.length > 0 && (
                        <Popover>
                            <PopoverTrigger asChild>
                                <button type="button" className={d.calendar.noDateTrigger}>
                                    <Iconify icon="eva:file-outline" width={14} />
                                    {t('posts.calendar.noDate', { count: postsWithoutDate.length })}
                                </button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-80 p-2 rounded-xl border border-[#e7e7ec] shadow-lg">
                                <p className="text-[11px] font-bold uppercase tracking-wider text-[#71717a] px-1 pb-2">
                                    {t('posts.calendar.noDateTitle')}
                                </p>
                                <div className="flex flex-col gap-1 max-h-72 overflow-y-auto">
                                    {postsWithoutDate.map((post) => renderPostChip(post, { inPopover: true }))}
                                </div>
                            </PopoverContent>
                        </Popover>
                    )}
                </div>
            </div>

            <div className={d.calendar.legend}>
                {['scheduled', 'published', 'draft', 'failed'].map((statusKey) => {
                    const cfg = STATUS_MAP[statusKey];
                    return (
                        <span key={statusKey} className={d.calendar.legendItem}>
                            <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                            {t(cfg.labelKey)}
                        </span>
                    );
                })}
            </div>

            <div className={d.calendar.body}>
                <div className={d.calendar.weekdayRow}>
                    {[0, 1, 2, 3, 4, 5, 6].map((dow) => (
                        <div key={dow} className={d.calendar.weekday}>{t(`posts.calendar.weekdays.${dow}`)}</div>
                    ))}
                </div>

                <div className={d.calendar.grid}>
                    {cells.map(({ date, inMonth }) => {
                        const key = dateKey(date);
                        const dayPosts = postsByDay[key] || [];
                        const visible = dayPosts.slice(0, MAX_VISIBLE_PER_DAY);
                        const hiddenCount = dayPosts.length - visible.length;
                        const isToday = isSameDay(date, today);

                        return (
                            <div
                                key={key}
                                className={`${d.calendar.dayCell} ${inMonth ? '' : d.calendar.dayCellOutside}`}
                                onClick={() => {
                                    if (!inMonth) return;
                                    onCreateAt?.(new Date(date.getFullYear(), date.getMonth(), date.getDate(), 9, 0));
                                }}
                                role="button"
                                tabIndex={inMonth ? 0 : -1}
                                title={inMonth ? t('posts.calendar.createHint') : undefined}
                                onKeyDown={(e) => {
                                    if (!inMonth || (e.key !== 'Enter' && e.key !== ' ')) return;
                                    e.preventDefault();
                                    onCreateAt?.(new Date(date.getFullYear(), date.getMonth(), date.getDate(), 9, 0));
                                }}
                            >
                                <div className="flex items-center justify-between gap-1 shrink-0">
                                    <span className={`${d.calendar.dayNumber} ${isToday ? d.calendar.dayNumberToday : inMonth ? 'text-[#52525b]' : 'text-[#c4c4cc]'}`}>
                                        {date.getDate()}
                                    </span>
                                    {inMonth && (
                                        <span className={d.calendar.dayAddHint} aria-hidden>
                                            <Iconify icon="eva:plus-outline" width={12} />
                                        </span>
                                    )}
                                </div>

                                <div className={d.calendar.dayPosts}>
                                    {visible.map((post) => renderPostChip(post))}
                                    {hiddenCount > 0 && (
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <button
                                                    type="button"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className={d.calendar.moreBtn}
                                                >
                                                    {t('posts.calendar.more', { count: hiddenCount })}
                                                </button>
                                            </PopoverTrigger>
                                            {renderDayPopover(date, dayPosts)}
                                        </Popover>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
